import { Box, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import MicroTextArea from "../../../../../../../../../components/MicroTextArea";
import Text from "../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useDebounce } from "../../../../../../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { useGetAllScreeningPreview } from "../../../../../../../../../queries/encounter/screening/use-get-all-screening-preview";
import { useRosPeTemplateArchive } from "../../../../../../../../../queries/encounter/use-ros-pe-template-archive";
import { useRosPeTemplateList } from "../../../../../../../../../queries/encounter/use-ros-pe-template-list";
import { useGetAllScreenerType } from "../../../../../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-screener-type";
import {
  DEF_PAGINATION,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../../../../../utils/utils";
import AssessmentDrawer from "../../../../../../scheduling/appointment/encounter/Assessment/AssessmentDrawer";
import PeImportDrawer from "../../../../../../scheduling/appointment/encounter/default-forms/PeImportDrawer";
import RosImportDrawer from "../../../../../../scheduling/appointment/encounter/default-forms/RosImportDrawer";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import StyledPopoverButton from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledPopoverButton";
import PreviewScreener from "../../../../../../scheduling/appointment/encounter/default-forms/form-2/ScreenerPreview";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import CptCodesField from "../components/form-components/CptCodesField";
import IcdCodesField from "../components/form-components/IcdCodesField";
import PreviewContentWrapper from "../components/form-components/PreviewContentWrapper";
import {
  CODE_FIELD_ROW_SX,
  PSYCH_PROGRESS_EXAM_FIELDS,
  PSYCH_PROGRESS_EXAM_PLAN_FIELDS,
  PSYCH_PROGRESS_SOAP_HEADING,
  PSYCH_PROGRESS_SUBHEADING,
  PSYCH_PROGRESS_SUBHEADING_SX,
} from "../contant/encounter-constant";

interface PsychiatricProgressExamProps {
  formik: FormikProps<Form2Payload>;
}

const PsychiatricProgressExam = ({ formik }: PsychiatricProgressExamProps) => {
  const [
    rosDisclosure,
    templateDisclosure,
    psychDisclosure,
    previewScreenerDisc,
  ] = useReduceDisclosure(4);
  const appointmentid = useSearchParams()[0].get("appointmentid");
  const { mutateAsync: archiveTemplate } = useRosPeTemplateArchive();

  function rosTemplateArchive<T>(value: boolean, id: T) {
    return archiveTemplate({
      encounterTemplateId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchRosTemplates();
      return res;
    });
  }

  const { data: screeners } = useGetAllScreeningPreview({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    appointmentUuid: appointmentid ?? "",
    xTENANTID: getXtenantId(),
  });

  const { data: rosTemplates, refetch: refetchRosTemplates } =
    useRosPeTemplateList({
      pagination: { page: 0, pageSize: 50 },
      gridSorting: [],
      archive: false,
      status: true,
      templateType: "ROS",
      appointmentUuid: appointmentid ?? undefined,
      xTENANTID: getXtenantId(),
    });

  const filteredRosTemplates = rosTemplates?.data?.content?.filter(
    (d: any) => !!d?.description?.toString().trim()
  );

  const [search, setSearchStringKey, instantSearchStringKey] =
    useDebounce<string>("");
  const { data: screenerTypeList } = useGetAllScreenerType({
    pagination: {
      page: 0,
      pageSize: 5,
    },
    xTENANTID: getXtenantId(),
    searchString: search,
  });

  const screenerList = useMemo(() => {
    return screenerTypeList?.data?.content?.map((item: any) => ({
      // Lower-casing first turned acronym screener types into title case
      // ("AAI" -> "Aai"); the type is labeled as-is, same as the Psychiatric
      // Note screening dropdown.
      value: item,
      label: replaceUnderScoreAddSpace(item),
    }));
  }, [screenerTypeList]);

  return (
    <Flex
      gap={2}
      flexDirection="column"
      p={1.5}
      sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <Grid container spacing={2}>
        {/* Subjective section. Headings must be Grid items — a bare Flex is not
            laid out by the container, so the md={6} fields after it wrap around
            the heading instead of starting on their own row. */}
        <Grid size={12}>
          <CustomTitle color={"primary"}>
            {PSYCH_PROGRESS_SOAP_HEADING.SUBJECTIVE}
          </CustomTitle>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTextArea
            label="Subjective Note"
            rows={3}
            variant="outlined"
            name="subjectiveChiefComplaint"
            placeholder="Enter Subjective Note"
            onBlur={formik.handleBlur}
            value={formik.values?.subjectiveChiefComplaint}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTextArea
            label="History of Present Illness (HPI)"
            rows={3}
            variant="outlined"
            name="hpi"
            placeholder="Enter HPI"
            onBlur={formik.handleBlur}
            value={formik.values?.hpi}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* <Flex flexDirection={"column"} width={"100%"}>
          {!!rosTemplates?.data?.content?.length && (
            <PreviewRosAndPeTemplate
              titleWidth="120px"
              dataList={rosTemplates?.data?.content}
              title="templateNameType"
              desc="description"
              onDelete={(uuid) => {
                templateArchive(true, uuid).then(() => {});
              }}
              onEdit={(med) => {
                allDisc.open(med);
                setActiveCompDrawerKey("rosTemp");
              }}
            />
          )}
        </Flex> */}

        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <PeImportDrawer disclosureHost={templateDisclosure} />
          <RosImportDrawer disclosureHost={rosDisclosure} />
          {(rosTemplates?.data?.content?.length ?? 0) < 1 && (
            <StyleAddButton
              w="100px"
              onClick={() => rosDisclosure.open()}
              label="Add ROS"
            />
          )}
          {!!filteredRosTemplates?.length && (
            <PreviewContentWrapper
              dataList={filteredRosTemplates}
              k="rosTemplate"
              title="ROS Templates"
              renderContent={(rosTemplate: any) => (
                <Text sx={{ color: "#424242" }}>
                  {`${rosTemplate?.templateNameType ?? "-"} : ${
                    rosTemplate?.description ?? "-"
                  }`}
                </Text>
              )}
              onDelete={(uuid) => {
                rosTemplateArchive(true, uuid).then(() => {});
              }}
              onEdit={(rosTemplate) => {
                templateDisclosure.open(rosTemplate);
              }}
            />
          )}
        </Grid>

        {/* Objective section — the Mental Status Exam fields below are its
            first sub-group, so they get their own sub-heading. */}
        <Grid size={12}>
          <Flex flexDirection="column" gap={1}>
            <CustomTitle color={"primary"}>
              {PSYCH_PROGRESS_SOAP_HEADING.OBJECTIVE}
            </CustomTitle>
            <Text sx={PSYCH_PROGRESS_SUBHEADING_SX}>
              {PSYCH_PROGRESS_SUBHEADING.MENTAL_STATUS_EXAM}
            </Text>
          </Flex>
        </Grid>

        {PSYCH_PROGRESS_EXAM_FIELDS.map((item) => (
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <CustomTextArea
              label={item.label}
              rows={3}
              variant="outlined"
              name={item.value}
              placeholder={`Enter ${item.label}`}
              onBlur={formik.handleBlur}
              value={formik.values?.[item.value]}
              onChange={formik.handleChange}
            />
          </Grid>
        ))}

        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTextArea
            label="Insight & Judgment"
            rows={3}
            variant="outlined"
            name="insightJudgment"
            placeholder="Enter Insight & Judgment"
            onBlur={formik.handleBlur}
            value={formik.values?.insightJudgment}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* Assessment section */}
        <Grid size={12}>
          <CustomTitle color={"primary"}>
            {PSYCH_PROGRESS_SOAP_HEADING.ASSESSMENT}
          </CustomTitle>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <AssessmentDrawer disclosureHost={psychDisclosure} />
          {/* note: this button is replaced from  psych template */}
          <Flex flexDirection={"column"} gap={1}>
            <MicroTextArea
              rows={4}
              label={"Assessment"}
              variant="outlined"
              name="assessmentFreeNote"
              placeholder="Assessment..."
              onBlur={formik.handleBlur}
              value={formik.values?.assessmentFreeNote || ""}
              onChange={formik.handleChange}
            />

            {!!Object.keys((screeners?.data ?? {}) as any)?.length && (
              <Flex flexDirection={"column"} width={"100%"} gap={1}>
                <Text fontWeight="bold" color="#424242" mb={1} ml={1}>
                  Assessment
                </Text>

                <PreviewScreener
                  onEdit={(it) => previewScreenerDisc.open(it)}
                  dataListObj={screeners?.data}
                />
              </Flex>
            )}
          </Flex>
          <Box
            sx={{
              width: "120px",
              mt: 1,
            }}
          >
            <StyledPopoverButton
              disableStartIcon
              onClick={(v) => psychDisclosure.open(v)}
              options={screenerList ?? []}
              label="Add Screening"
              instantSearchStringKey={instantSearchStringKey}
              setSearchStringKey={setSearchStringKey}
            />
          </Box>
        </Grid>

        {/* ICD codes close the Assessment section, the way the report does. */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <Flex
            flexDirection="row"
            gap={2}
            flexWrap="wrap"
            sx={CODE_FIELD_ROW_SX}
          >
            <IcdCodesField formik={formik} />
          </Flex>
        </Grid>

        {/* Plan section */}
        <Grid size={12}>
          <CustomTitle color={"primary"}>
            {PSYCH_PROGRESS_SOAP_HEADING.PLAN}
          </CustomTitle>
        </Grid>

        {/* Therapy leads the Plan band in the report, so it does the same here
            instead of sitting under Subjective. */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTextArea
            label="Therapy"
            rows={3}
            variant="outlined"
            name="therapy"
            placeholder="Enter Therapy"
            onBlur={formik.handleBlur}
            value={formik.values?.therapy}
            onChange={formik.handleChange}
          />
        </Grid>

        {PSYCH_PROGRESS_EXAM_PLAN_FIELDS.map((item) => (
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <CustomTextArea
              label={item.label}
              rows={3}
              variant="outlined"
              name={item.value}
              placeholder={`Enter ${item.label}`}
              onBlur={formik.handleBlur}
              value={formik.values?.[item.value]}
              onChange={formik.handleChange}
            />
          </Grid>
        ))}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <MicroTextArea
            rows={4}
            variant="outlined"
            name="note"
            placeholder="Note..."
            label="Enter Note"
            onBlur={formik.handleBlur}
            value={formik.values?.note || ""}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* CPT codes sit directly below Enter Note, closing the Plan fields
            before the Psychoeducation Session block. */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <Flex
            flexDirection="row"
            gap={2}
            flexWrap="wrap"
            sx={CODE_FIELD_ROW_SX}
          >
            <CptCodesField formik={formik} />
          </Flex>
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="psychoeducationSessionRequired"
                checked={!!formik.values?.psychoeducationSessionRequired}
                onChange={(e) =>
                  formik.setFieldValue(
                    "psychoeducationSessionRequired",
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Text variant="body1" fontWeight={500}>
                Psychoeducation Session Required?
              </Text>
            }
          />
        </Grid>
        {formik.values?.psychoeducationSessionRequired && (
          <Grid size={12}>
            <PsychoeducationTherapyForm formik={formik} />
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default PsychiatricProgressExam;
